/**
 * «ملف الدولة» on the screen: money in the institution's currency, times on
 * its clock, in the language the person reads.
 *
 * The profile comes from the server (GET /api/auth/policy before sign-in,
 * /capabilities after: the institution's `countryProfile` policy, or the
 * platform's) and is made active here by I18nProvider. Until it arrives the
 * default is Egypt's — what every screen printed before — so nothing on a
 * screen changes for an institution that kept the default.
 *
 * Pure apart from the one active profile, so the 200-odd places that print an
 * amount through formatCurrency (domain/pricing.js) and formatMoney
 * (hospitalShared/labels.js) follow the institution without each being told.
 */

/** Egypt, as the server's lib/countryProfile.js describes it. The parity test keeps them equal. */
export const DEFAULT_COUNTRY = Object.freeze({
  code: 'EG',
  name: 'مصر',
  nameEn: 'Egypt',
  currency: 'EGP',
  currencySymbol: 'ج.م',
  currencySymbolEn: 'EGP',
  timezone: 'Africa/Cairo',
  timezoneLabel: 'توقيت القاهرة',
  timezoneLabelEn: 'Cairo time',
  locale: 'ar-EG',
  localeEn: 'en-EG',
  dialCode: '20',
  mobile: Object.freeze({ local: '^01[0125]\\d{8}$', example: '01012345678', digits: 11 }),
  idTypes: Object.freeze(['national']),
});

let active = { country: DEFAULT_COUNTRY, lang: 'ar' };

/** Make a profile (and a language) the one every format below uses. */
export function setActiveLocale({ country, lang } = {}) {
  active = {
    country: country && country.code ? { ...DEFAULT_COUNTRY, ...country } : active.country,
    lang: lang === 'en' || lang === 'ar' ? lang : active.lang,
  };
  return active;
}

export const activeLocale = () => active;
export const activeCountry = () => active.country;
export const activeLang = () => active.lang;

const numberOf = (value) => {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

/** "ج.م" / "ر.س" in Arabic, "EGP" / "SAR" in English. */
export function currencyLabel({ country = active.country, lang = active.lang } = {}) {
  return lang === 'en' ? country.currencySymbolEn || country.currency : country.currencySymbol;
}

/**
 * "1,250 ر.س" / "SAR 1,250". `digits: 'arab'` writes Arabic-Indic numerals the
 * way the desks' receipts always have (١٬٢٥٠); the default is Western digits.
 */
export function formatMoney(amount, { country = active.country, lang = active.lang, digits = 'latn', decimals = 2 } = {}) {
  const n = numberOf(amount);
  const tag = lang === 'en' ? 'en-US' : digits === 'arab' ? 'ar-EG' : 'en-US';
  const figure = n.toLocaleString(tag, { minimumFractionDigits: 0, maximumFractionDigits: decimals });
  return lang === 'en' ? `${currencyLabel({ country, lang })} ${figure}` : `${figure} ${currencyLabel({ country, lang })}`;
}

/** A date or a time on the institution's clock, in the reader's language. */
export function formatDateTime(value, { country = active.country, lang = active.lang, dateStyle = 'medium', timeStyle = 'short' } = {}) {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const options = { timeZone: country.timezone };
  if (dateStyle) options.dateStyle = dateStyle;
  if (timeStyle) options.timeStyle = timeStyle;
  try {
    // Western digits in both languages, as every other date on the screens is written.
    return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'ar-EG-u-nu-latn', options).format(date);
  } catch {
    return date.toISOString().slice(0, 16).replace('T', ' ');
  }
}

/** A count, "1,250", in Western digits whichever the language (the tiles and the money do the same). */
export const formatCount = (value) => numberOf(value).toLocaleString('en-US', { maximumFractionDigits: 0 });

export const formatDate = (value, opts = {}) => formatDateTime(value, { ...opts, timeStyle: null });
export const formatTime = (value, opts = {}) => formatDateTime(value, { ...opts, dateStyle: null });

/** "توقيت الرياض" / "Riyadh time". */
export const timezoneLabel = ({ country = active.country, lang = active.lang } = {}) =>
  lang === 'en' ? country.timezoneLabelEn || country.timezone : country.timezoneLabel || country.timezone;

/** Is this a mobile number of the active country? (The server checks again.) */
export function isMobileOf(raw, country = active.country) {
  const digits = String(raw || '')
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/[^\d+]/g, '');
  let local = digits;
  if (local.startsWith('+')) local = local.slice(1);
  if (local.startsWith('00')) local = local.slice(2);
  if (local.startsWith(country.dialCode)) local = `0${local.slice(country.dialCode.length)}`;
  return new RegExp(country.mobile.local).test(local);
}
